<template>
  <div ref="$container">
    <Teleport to="body" :disabled="!isFullScreen">
      <div class="grid grid-cols-2 gap-1.5 sm:gap-2" v-if="isGrid">
        <div
          class="rounded-3xl rounded-b-md relative col-span-1"
          :class="[
            isOdd ? 'even:top-6' : ''
          ]"
          v-for="(post, i) in posts"
        >
          <StoryItem
            :post="post"
            size="md"
            is-skip
            :is-mine="post.owner === currentUser?._id"
            :is-greyed="false"
            @click.stop="() => { position = i; isFullScreenAction = true; }"
          />
        </div>

        <SkeletonBase v-for="i in perPage" is-animated class="aspect-[12/16] rounded-3xl !bg-whale-950/10 dark:!bg-dark-50/5" :class="[ isOdd ? 'even:top-6' : '' ]" v-show="isPostsLoading" />

        <div class="col-span-2" v-if="posts.length <= 0 && !isPostsLoading">
          <slot name="empty" />
        </div>
        <template v-else>
          <div class="col-span-2 h-4 " />

          <template v-if="!isEnd">
            <div class="col-span-2 text-center pt-2">
              <ButtonLink @click="page++">{{ $t('actions.showMore') }}</ButtonLink>
            </div>
          </template>
        </template>
      </div>
      
      <div :class="[ isFullScreen ? 'fixed top-0 right-0 w-full h-full bg-charcoal-950 !mt-0' : '-mx-2.5' ]" :style="isFullScreen ? { zIndex, transformStyle: 'preserve-3d', backfaceVisibility: 'hidden' } : {}" v-if="!isGrid || isFullScreen">
        <CarouselSwipe
          v-model="position"
          transition="ease-out"
          :transition-duration="100"
          :active-offset="isFullScreen ? 1 : 6"
          :class="[ isFullScreen ? 'h-full' : 'px-2' ]"
          align="left"
          pagination="none"
          :order="[
            'create',
            ...postsOrder,
            ...(new Array(perPage).fill('a').map((v, i) => `skeleton-${i + 1}`))
          ]"
          :hidden="isPostsLoading ? [] : new Array(perPage).fill('a').map((v, i) => `skeleton-${i + 1}`)"
          :width="isFullScreen ? 'full' : 70"
          :spacing="isFullScreen ? 0 : 3"
        >
          <template #create v-if="isCreate && !isFullScreen">
            <!-- <NuxtLink class="aspect-[12/16] flex flex-col justify-center text-center items-center text-2xs font-bold leading-4 p-4 bg-light-300 dark:bg-dark-900 rounded-2xl overflow-hidden space-y-1.5 relative" :to="{ query: { storyCreate: 'true' } }">
              <img :src="currentUser?.image?.getSize('m')" class="absolute opacity-10 w-full h-full object-cover" v-if="currentUser?.image" />

              <IconBase name="cute/plus" :width="20" class="relative z-10" />
              <p class="relative z-10 leading-3 mt-1">{{ $t('components.Story.create.action') }}</p>
            </NuxtLink> -->
            <NuxtLink :to="{ query: { storyCreate: 'true' } }" class="size-16 bg-dark-950 text-white relative overflow-hidden rounded-xl flex flex-col items-center justify-center">
              <img :src="currentUser?.image?.getSize('m')" class="absolute opacity-50 w-full h-full object-cover" v-if="currentUser?.image" />

              <IconBase name="cute/plus-fill" :width="18" class="relative z-10" />
              <p class="text-4xs font-bold leading-tight relative">Story</p>
            </NuxtLink>
          </template>
        
          <template v-for="(post, i) in posts" v-slot:[post._id]="{ isSlotActive, isSlotCurrent }" #[post._id]>
            <StoryItem
              size="sm"
              :post="post"
              @click.stop="() => { position = i; isFullScreenAction = true; }"
              v-if="isSlotActive && !isFullScreen"
            />
            <StoryDisplay
              :post="post"
              :is-active="isSlotActive"
              :is-current="isSlotCurrent"
              @close="onClose"
              @next="position++"
              @prev="position--"
              v-if="isSlotActive && isFullScreen"
            />
          </template>
          <template v-for="i in perPage" #[`skeleton-${i}`]>
            <SkeletonBase is-animated class="size-16 rounded-2xl flex items-center justify-center !bg-whale-950/10 dark:!bg-dark-50/5" />
          </template>
        </CarouselSwipe>
      </div>
    </Teleport>

    <div class="fixed bottom-app-bar z-[100] left-app-side right-0" :class="[ isShowUnseen ? '' : 'pointer-events-none' ]" v-if="anchor && !isFullScreen">
      <img src="@/assets/img/gradients/rainbow.webp" class="!max-w-none w-full mx-auto h-full absolute bottom-0 left-1/2 -translate-x-1/2 rotate-180 transition duration-1000 delay-200 ease-in-out pointer-events-none" :class="[ isShowUnseen ? 'opacity-80' : 'opacity-0' ]" />

      <div class="relative text-center z-20 flex items-center justify-center p-4 transition duration-500 ease-in-out" :class="[ isShowUnseen ? '' : 'translate-y-full opacity-0' ]">
        <ButtonBase
          size="sm"
          color="royal"
          :href="anchor"
          @click="() => isShowUnseen = false"
        >
          {{ $t('components.Story.new') }}
        </ButtonBase>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { IPost, Post } from '@/classes'
import UserStore from '@/store/user'
import PostsStore from '@/store/posts'
import PopinStore from '@/store/popin'
import UsersStore from '@/store/users'
import WindowStore from '@/store/window'
import { storiesGetAPI } from '@/api/post'
import { UserGenders } from '@/classes/UserProfile'
import { setStatusBarColor } from '~/utils/native/useLayout'

const props = withDefaults(defineProps<{
  isGrid?: boolean  
  category?: 'following' | 'popular' | 'new'
  perPage?: number
  isHeader?: boolean
  isCreate?: boolean
  isOdd?: boolean
  isNetwork?: boolean
  isFaces?: boolean
  isSuggested?: boolean
  isForceFullscreen?: boolean
  scrollRef?: HTMLElement | 'window'
  anchor?: string
  isSpicy?: boolean

  userId?: string
  distance?: number
  genders?: (typeof UserGenders[number])[]
}>(), {
  category: 'following',
  scrollRef: 'window',
  isGrid: false,
  isSpicy: undefined,
  isOdd: true,
  anchor: '',
  isHeader: false,
  isForceFullscreen: false,
  isNetwork: false,
  isSuggested: false,
  isFaces: false,
  isCreate: false,
  perPage: 5,
  distance: 9999,
  genders: () => [],
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

useKeepAlive({
  onStart: () => {
    isShownOnce.value = false
    isEnd.value = false
    postsRaw.value = []
    page.value = 0
    position.value = 0
    
    refresh()
  }
})

const isFullScreenAction = ref(false)

const windowStore = WindowStore()
const { titles, isLockNavigation, windowWidth } = storeToRefs(windowStore)
const popinStore = PopinStore()
const { popinZIndex } = storeToRefs(popinStore)
const postsStore = PostsStore()
const usersStore = UsersStore()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const position = ref(0)
const zIndex = ref(popinZIndex.value)

const isFullScreen = computed(() => props.isForceFullscreen || isFullScreenAction.value)

const { watchersAdd, watchersStop, watchersRestart } = useWatchers()
const { category, distance, genders, userId, isNetwork, isSpicy } = toRefs(props)

watchersAdd(() => watch(isFullScreen, () => {
  if (isFullScreen.value) {
    popinZIndex.value++
    zIndex.value = popinZIndex.value
  }
}, { immediate: true }))

const route = useRoute()
const page = ref(0)

const isEnd = ref(false)
const isLoaded = ref(false)

const postsRaw: Ref<IPost[]> = ref([])
const posts: Ref<Post[]> = computed(() => postsStore.getItems(postsRaw.value.map(i => i._id)))

const { isComponentActive } = useKeepAlive({
  onStart: () => {
    window.addEventListener('popstate', onPopState)
  },
  onActivated: () => {
    watchersRestart()
  },
  onLeave: () => {
    watchersStop()
    postsRaw.value = postsRaw.value.slice(0, props.perPage)
    window.removeEventListener('popstate', onPopState)

    if (abort.value) abort.value.abort('cancel')
  }
})

const onPopState = () => {
  if (isFullScreen.value) {
    isLockNavigation.value = true
    isFullScreenAction.value = false
  }
}

const abort = ref<AbortController | null>(null)

const postsOrder: Ref<string[]> = ref([])

const { pending: isPostsLoading, refresh } = useLazyAsyncData(Math.random().toString(), async () => { 
  if (abort.value) abort.value.abort('cancel')
  abort.value = new AbortController()

  const response = await storiesGetAPI({
    isSpicy: props.isSpicy,
    category: props.userId ? 'user' : props.category,
    ...(props.userId ? { user: props.userId } : {}),
  }, {
    page: page.value,
    per_page: props.perPage,
    exclude: posts.value.map(i => i._id)
  }, abort.value)

  if (response.ok && response.data?.items) {
    if (response.data.items.length < props.perPage) {
      isEnd.value = true
    }

    response.data.items.forEach(item => postsStore.setItem(item))
    if (response.data.users) response.data.users.forEach(user => usersStore.setItem(user))

    postsOrder.value = [
      ...(page.value > 0 ? postsOrder.value : []),
      ...response.data.items.map(i => i._id)
    ]

    postsRaw.value = [
      ...(page.value > 0 ? postsRaw.value : []),
      ...response.data.items
    ]
  }

  isLoaded.value = true

  return true
}, {
  immediate: true,
  server: false
})

watch(userId, () => {
  onClose()
})

watchersAdd(() => watch([ category, distance, genders, isNetwork, isSpicy, userId ], () => {
  isEnd.value = false
  postsRaw.value = []

  if (page.value === 0) {
    refresh()
  } else {
    page.value = 0
  }
}))

watchersAdd(() => watch(page, () => refresh()))

const $container = ref()
const { arrivedState } = useScroll(props.scrollRef === 'window' ? window : props.scrollRef, {
  offset: { bottom: window.innerHeight * 1.5 }
})

watchersAdd(() => watch([ position, arrivedState ], () => {
  if ((isComponentActive.value && isLoaded.value && !isEnd.value && !isPostsLoading.value) && ((position.value > posts.value.length - 4) || (props.isGrid && arrivedState.bottom))) page.value += 1
}, { deep: true }))

// CONTEXT

const postCurrent = computed(() => posts.value[position.value])
const userCurrent = computed(() => usersStore.getItem(postCurrent.value?.owner))

watchersAdd(() => watch([ isEnd, postsRaw ], () => {
  if (isEnd.value && postsRaw.value.length === 0) onClose()
}))

watchersAdd(() => watch([ position, isFullScreen ], () => {
  if (isFullScreen.value) {
    useNuxtApp().$amplitude.logEvent(`Story_Open`, {
      source: route.fullPath
    })
  }
}))

const router = useRouter()

watchersAdd(() => watch(() => route.query.storyCreated, (v) => {
  if (v) {
    router.replace({ query: { ...route.query, storyCreated: undefined } })

    if (page.value > 0) {
      page.value = 0
    } else {
      refresh()
    }
  }
}))

watchersAdd(() => watch([postCurrent, isFullScreen], () => {
  if (isFullScreen.value && postCurrent) {
    titles.value.set('story', {
      value: useNuxtApp().$i18n.t('components.Story.title', { name: userCurrent.value?.username ?? '' }),
      createdAt: new Date()
    })

    setStatusBarColor('dark')
  } else {
    titles.value.delete('story')
    setStatusBarColor(route.meta?.statusBarColor === 'dark' ? 'dark' : 'light')
  }
}))

const onClose = () => {
  isFullScreenAction.value = false
  titles.value.delete('story')
  emit('close')
}

const isShowUnseen = ref(false)
const isShownOnce = ref(false)

watchersAdd(() => watch(posts, () => {
  const unseen = posts.value.filter(p => p.sentimentCount === 0).length

  if (!isShownOnce.value && unseen > 0) {
    isShowUnseen.value = true
    isShownOnce.value = true

    setTimeout(() => {
      isShowUnseen.value = false
    }, 5000)
  }
}))

// HOOKS

</script>